import type { ActivityEventCategory } from '../types/user-journey.types';

export interface CollapsedEventChip {
  eventType: string;
  count: number;
  category: ActivityEventCategory;
}

const CATEGORY_BY_EVENT_TYPE: Record<string, ActivityEventCategory> = {
  generate: 'generation',
  generation: 'generation',
  regenerate: 'generation',
  edit: 'edit',
  resize: 'edit',
  download: 'download',
  export: 'download',
  failure: 'failure',
  failed: 'failure',
  error: 'failure',
  login: 'setup',
  logout: 'setup',
  signup: 'setup',
  auth: 'setup',
  setup: 'setup',
};

const CATEGORY_KEYWORDS: Array<{ keywords: string[]; category: ActivityEventCategory }> = [
  { keywords: ['generate', 'generation', 'regenerat'], category: 'generation' },
  { keywords: ['edit', 'resize'], category: 'edit' },
  { keywords: ['download', 'export'], category: 'download' },
  { keywords: ['fail', 'error'], category: 'failure' },
  { keywords: ['login', 'logout', 'signup', 'auth', 'setup', 'verify'], category: 'setup' },
];

export function inferEventCategory(eventType: string, explicitCategory?: ActivityEventCategory): ActivityEventCategory {
  if (explicitCategory) {
    return explicitCategory;
  }

  const normalised = eventType.trim().toLowerCase();
  if (CATEGORY_BY_EVENT_TYPE[normalised]) {
    return CATEGORY_BY_EVENT_TYPE[normalised];
  }

  for (const mapping of CATEGORY_KEYWORDS) {
    if (mapping.keywords.some((keyword) => normalised.includes(keyword))) {
      return mapping.category;
    }
  }

  return 'setup';
}

export function formatEventTypeLabel(eventType: string): string {
  return eventType
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function collapseConsecutiveEventTypes(
  eventTypes: string[],
  getCategory: (eventType: string) => ActivityEventCategory = (eventType) => inferEventCategory(eventType),
): CollapsedEventChip[] {
  if (eventTypes.length === 0) {
    return [];
  }

  const collapsed: CollapsedEventChip[] = [];
  let currentType = eventTypes[0];
  let currentCount = 1;

  for (let index = 1; index < eventTypes.length; index += 1) {
    const eventType = eventTypes[index];
    if (eventType === currentType) {
      currentCount += 1;
      continue;
    }

    collapsed.push({
      eventType: currentType,
      count: currentCount,
      category: getCategory(currentType),
    });
    currentType = eventType;
    currentCount = 1;
  }

  collapsed.push({
    eventType: currentType,
    count: currentCount,
    category: getCategory(currentType),
  });

  return collapsed;
}

export function normaliseSessionEventChips(
  events: Array<{ event_type: string; count?: number }>,
): CollapsedEventChip[] {
  const expandedTypes = events.flatMap((event) =>
    Array.from({ length: event.count ?? 1 }, () => event.event_type),
  );

  return collapseConsecutiveEventTypes(expandedTypes);
}

export const EVENT_CATEGORY_STYLES: Record<ActivityEventCategory, { chip: string; legend: string }> = {
  generation: {
    chip: 'border-[#DDD6FE] bg-[#F5F3FF] text-[#7C3AED]',
    legend: 'bg-[#7C3AED]',
  },
  edit: {
    chip: 'border-[#99F6E4] bg-[#F0FDFA] text-[#0F766E]',
    legend: 'bg-[#14B8A6]',
  },
  download: {
    chip: 'border-[#A7F3D0] bg-[#ECFDF5] text-[#059669]',
    legend: 'bg-[#059669]',
  },
  failure: {
    chip: 'border-[#FECACA] bg-[#FEF2F2] text-[#DC2626]',
    legend: 'bg-[#DC2626]',
  },
  setup: {
    chip: 'border-[#D9DBE3] bg-[#F7F8FA] text-[#5E6278]',
    legend: 'bg-[#9CA3AF]',
  },
};

export const EVENT_CATEGORY_LABELS: Record<ActivityEventCategory, string> = {
  generation: 'Generation',
  edit: 'Edit / resize',
  download: 'Download / export',
  failure: 'Failures',
  setup: 'Setup / auth',
};

export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

export function formatRoundedMetric(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }

  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }

  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

export function getUserInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function normaliseFlags(
  flags: Array<string | { code: string; label: string }> | undefined,
): Array<{ code: string; label: string }> {
  if (!flags?.length) {
    return [];
  }

  return flags.map((flag) => {
    if (typeof flag === 'string') {
      return {
        code: flag,
        label: flag
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (char) => char.toUpperCase()),
      };
    }
    return flag;
  });
}
